import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { clientsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw, Plus, Play, Pause, Trash2, Calendar, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecurringPage = () => {
  const [loading, setLoading] = useState(true);
  const [recurring, setRecurring] = useState([]);
  const [clients, setClients] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    client_id: "",
    frequency: "monthly",
    day_of_month: 1,
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    auto_send_email: true,
    items: [{ description: "", quantity: 1, unit_price: 0, unit: "PZA", clave_producto: "01010101", clave_unidad: "H87" }],
    payment_method: "PUE",
    payment_form: "03",
    notes: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const [recurringRes, clientsRes] = await Promise.all([
        axios.get(`${API}/recurring-invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        clientsApi.list()
      ]);
      setRecurring(recurringRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!form.client_id) {
      toast.error("Selecciona un cliente");
      return;
    }
    
    if (form.items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error("Completa todos los conceptos");
      return;
    }
    
    setCreating(true);
    
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(`${API}/recurring-invoices`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Facturación recurrente configurada");
      setShowCreateDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al configurar");
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const token = localStorage.getItem("cia_token");
      await axios.put(`${API}/recurring-invoices/${id}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Estado actualizado a ${newStatus}`);
      loadData();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const handleGenerateNow = async (id) => {
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(`${API}/recurring-invoices/${id}/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Factura generada exitosamente");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar factura");
    }
  };

  const resetForm = () => {
    setForm({
      client_id: "",
      frequency: "monthly",
      day_of_month: 1,
      start_date: new Date().toISOString().split("T")[0],
      end_date: "",
      auto_send_email: true,
      items: [{ description: "", quantity: 1, unit_price: 0, unit: "PZA", clave_producto: "01010101", clave_unidad: "H87" }],
      payment_method: "PUE",
      payment_form: "03",
      notes: ""
    });
  };

  const addItem = () => {
    setForm({
      ...form,
      items: [...form.items, { description: "", quantity: 1, unit_price: 0, unit: "PZA", clave_producto: "01010101", clave_unidad: "H87" }]
    });
  };

  const removeItem = (index) => {
    if (form.items.length === 1) return;
    setForm({
      ...form,
      items: form.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index][field] = value;
    setForm({ ...form, items: newItems });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
  };

  const calculateTotal = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    return subtotal * 1.16;
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      weekly: "Semanal",
      monthly: "Mensual",
      quarterly: "Trimestral",
      yearly: "Anual"
    };
    return labels[freq] || freq;
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-100 text-green-800",
      paused: "bg-yellow-100 text-yellow-800",
      cancelled: "bg-red-100 text-red-800"
    };
    const labels = {
      active: "Activa",
      paused: "Pausada",
      cancelled: "Cancelada"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#5B9BD5]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recurring-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Facturación Recurrente</h1>
          <p className="text-slate-600">Automatiza tus facturas periódicas</p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="cia-btn-primary" data-testid="create-recurring-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Recurrente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Configurar Facturación Recurrente</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cliente *</Label>
                  <Select value={form.client_id} onValueChange={(value) => setForm({ ...form, client_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Frecuencia</Label>
                  <Select value={form.frequency} onValueChange={(value) => setForm({ ...form, frequency: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="monthly">Mensual</SelectItem>
                      <SelectItem value="quarterly">Trimestral</SelectItem>
                      <SelectItem value="yearly">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Día del mes (1-28)</Label>
                  <Input
                    type="number"
                    value={form.day_of_month}
                    onChange={(e) => setForm({ ...form, day_of_month: Math.min(28, Math.max(1, parseInt(e.target.value) || 1)) })}
                    min="1"
                    max="28"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha inicio</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha fin (opcional)</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Conceptos</Label>
                {form.items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-end p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <Label className="text-xs">Descripción</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => updateItem(index, "description", e.target.value)}
                        placeholder="Servicio mensual"
                      />
                    </div>
                    <div className="w-20">
                      <Label className="text-xs">Cantidad</Label>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, "quantity", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="w-32">
                      <Label className="text-xs">Precio</Label>
                      <Input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addItem} className="w-full">
                  <Plus className="w-4 h-4 mr-2" /> Agregar concepto
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="auto_send"
                  checked={form.auto_send_email}
                  onChange={(e) => setForm({ ...form, auto_send_email: e.target.checked })}
                />
                <Label htmlFor="auto_send">Enviar factura por correo automáticamente</Label>
              </div>

              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-sm">Total por período: <strong>{formatCurrency(calculateTotal(form.items))}</strong></p>
              </div>

              <Button type="submit" className="w-full cia-btn-primary" disabled={creating}>
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Configurar Recurrente"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Recurring List */}
      {recurring.length === 0 ? (
        <Card className="cia-card">
          <CardContent className="p-12 text-center">
            <RefreshCw className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Sin facturas recurrentes</h3>
            <p className="text-slate-500">Configura facturas automáticas para tus clientes frecuentes</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {recurring.map((item) => (
            <Card key={item.id} className="cia-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#5B9BD5]/10 flex items-center justify-center">
                      <RefreshCw className="w-6 h-6 text-[#5B9BD5]" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{item.client_name}</p>
                      <p className="text-sm text-slate-500">
                        {getFrequencyLabel(item.frequency)} • Día {item.day_of_month}
                      </p>
                      <p className="text-sm text-slate-500">
                        Próxima: {new Date(item.next_invoice_date).toLocaleDateString('es-MX')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold text-[#5B9BD5]">
                        {formatCurrency(calculateTotal(item.items || []))}
                      </p>
                      <p className="text-xs text-slate-500">{item.total_generated} generadas</p>
                    </div>
                    
                    {getStatusBadge(item.status)}
                    
                    <div className="flex gap-2">
                      {item.status === "active" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(item.id, "paused")}
                          title="Pausar"
                        >
                          <Pause className="w-4 h-4" />
                        </Button>
                      ) : item.status === "paused" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(item.id, "active")}
                          title="Reactivar"
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      ) : null}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleGenerateNow(item.id)}
                        title="Generar ahora"
                      >
                        <Calendar className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusChange(item.id, "cancelled")}
                        className="text-red-600"
                        title="Cancelar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecurringPage;
